// frontend/src/utils/professionHint.js
//
// THE PROFESSION HINT - a few words a signed-out visitor typed into the
// conversion popup ("what do you do?"), carried across sign-up so the
// first-login profession box can start with them instead of empty.
//
// IT IS A CONVENIENCE, NEVER A FACT. The text is free text a person wrote about
// their own business: it is stored ONLY in this browser, it never travels
// anywhere except through the user's own normal profession submit (the welcome
// dialog or the dashboard's inline field), and it is never logged, never sent
// to analytics and never written into the URL.
//
// SHAPE in localStorage under `lf_profession_hint`:
//   {"v": 1, "text": "<sanitized>", "savedAt": <ms epoch>}
//
// WHO MAY READ IT BACK. A shared device is the case that matters: a visitor
// types a hint, then someone signs in to an OLDER account on the same browser.
// That account must never be handed a stranger's words, so a hint is returned
// only to an account created AFTER it was typed (minus 60s of clock slack -
// the account row is stamped by the server, the hint by this browser). With a
// 24h TTL on top, an abandoned hint cannot linger.
//
// NEVER THROWS. Every export runs inside render paths (a lazy `useState`
// initializer) or a logout teardown; a blocked storage (Safari private mode,
// disabled site data) or a hostile value must degrade to "no hint", never take
// a screen down.

import { PROFESSION_MAX_LENGTH } from "../content/professionExamples";
import { hasProfession, isProfessionInReview } from "./onboardingSelectors";

/** The single storage key. Its only writer and readers are this module. */
export const PROFESSION_HINT_KEY = "lf_profession_hint";

/** Schema version of the stored object. A different `v` is treated as invalid. */
const HINT_VERSION = 1;

/** A hint older than this is expired and deleted on read. */
export const PROFESSION_HINT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Clock slack between the server's `users.created_at` and this browser's
 * `savedAt`. Also bounds how far in the FUTURE a stored `savedAt` may sit - a
 * future stamp would otherwise stretch the TTL indefinitely.
 */
export const PROFESSION_HINT_SKEW_MS = 60 * 1000;

// C0 controls except LF (a textarea description may legitimately span lines),
// DEL, and the C1 block. Tab is mapped to a space before this runs.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/g;

/**
 * Normalise free text to what the hint may hold: line endings unified to LF,
 * tabs to spaces, every other control character removed, trimmed, and capped
 * at PROFESSION_MAX_LENGTH without leaving half a surrogate pair at the cut.
 *
 * @param {*} value
 * @returns {string} "" for a non-string or an all-whitespace value.
 */
export function sanitizeProfessionHint(value) {
  if (typeof value !== "string") return "";
  let text = value
    .replace(/\r\n?/g, "\n")
    .replace(/\t/g, " ")
    .replace(CONTROL_CHARS, "")
    .trim();
  if (text.length > PROFESSION_MAX_LENGTH) {
    text = text.slice(0, PROFESSION_MAX_LENGTH);
    // A cut between the two halves of an astral character (an emoji) would
    // leave a lone high surrogate - drop it rather than store a broken glyph.
    const last = text.charCodeAt(text.length - 1);
    if (last >= 0xd800 && last <= 0xdbff) text = text.slice(0, -1);
    text = text.trim();
  }
  return text;
}

/**
 * An instant as ms epoch, from an ISO string, a Date or a number.
 *
 * @param {*} value
 * @returns {number} NaN when the value is not a real instant.
 */
function toEpochMs(value) {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (typeof value === "string" && value) return Date.parse(value);
  return NaN;
}

/** localStorage, or null where it does not exist or cannot even be reached. */
function storage() {
  try {
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage
      : null;
  } catch {
    return null;
  }
}

/**
 * Store the visitor's hint. An empty (or all-control) value stores nothing.
 *
 * @param {string} text
 * @returns {void}
 */
export function saveProfessionHint(text) {
  try {
    const clean = sanitizeProfessionHint(text);
    if (!clean) return;
    const store = storage();
    if (!store) return;
    store.setItem(
      PROFESSION_HINT_KEY,
      JSON.stringify({ v: HINT_VERSION, text: clean, savedAt: Date.now() })
    );
  } catch {
    /* blocked or full storage - the hint is a convenience, nothing is lost */
  }
}

/**
 * Remove the hint. Called after a successful profession submit and on logout.
 *
 * @returns {void}
 */
export function clearProfessionHint() {
  try {
    storage()?.removeItem(PROFESSION_HINT_KEY);
  } catch {
    /* blocked storage - nothing was readable there either */
  }
}

/**
 * Parse and validate the stored entry.
 *
 * @param {string|null} raw
 * @returns {{text: string, savedAt: number}|null} null for anything that is
 *   not exactly the v1 shape with a usable text.
 */
function parseEntry(raw) {
  if (typeof raw !== "string" || !raw) return null;
  let entry;
  try {
    entry = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
  if (entry.v !== HINT_VERSION) return null;
  if (typeof entry.text !== "string") return null;
  if (typeof entry.savedAt !== "number" || !Number.isFinite(entry.savedAt)) return null;
  // Over-long stored text is a tampered or foreign entry, not one to truncate.
  if (entry.text.length > PROFESSION_MAX_LENGTH) return null;
  const text = sanitizeProfessionHint(entry.text);
  if (!text) return null;
  return { text, savedAt: entry.savedAt };
}

/**
 * The hint for THIS account, or null.
 *
 * Returns the text only when the entry is a valid v1 object, at most 24h old,
 * not stamped in the future, and the account was created no earlier than 60s
 * before the hint was typed. An invalid or expired entry is deleted; a valid
 * entry that merely does not belong to this account is left alone (a new
 * account signed up next on this browser may still be its owner).
 *
 * @param {object} [options]
 * @param {string|Date|number} [options.userCreatedAt] the account's
 *   `created_at` (ISO string from the API, a Date, or ms epoch).
 * @returns {string|null}
 */
export function readProfessionHint({ userCreatedAt } = {}) {
  try {
    const store = storage();
    if (!store) return null;
    const raw = store.getItem(PROFESSION_HINT_KEY);
    if (raw === null) return null;

    const entry = parseEntry(raw);
    const now = Date.now();
    if (
      !entry ||
      now - entry.savedAt > PROFESSION_HINT_TTL_MS ||
      entry.savedAt - now > PROFESSION_HINT_SKEW_MS
    ) {
      clearProfessionHint();
      return null;
    }

    const createdAt = toEpochMs(userCreatedAt);
    // Fail closed: an account whose age we cannot read is not proven new.
    if (!Number.isFinite(createdAt)) return null;
    if (createdAt < entry.savedAt - PROFESSION_HINT_SKEW_MS) return null;
    return entry.text;
  } catch {
    return null;
  }
}

/**
 * The hint to SEED a first-login profession box with, for this user, or null.
 *
 * The one read both field owners (the welcome dialog and the dashboard's inline
 * field) make, from a lazy `useState` initializer. Never for a user who already
 * has a profession or one waiting in review: their box is not a first ask, so
 * there is nothing to pre-fill.
 *
 * @param {object|null} user the current user payload (`created_at` required).
 * @returns {string|null}
 */
export function readProfessionHintForUser(user) {
  if (!user || hasProfession(user) || isProfessionInReview(user)) return null;
  return readProfessionHint({ userCreatedAt: user.created_at });
}
